import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  ReloadIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import Stepper from './Stepper';
import {
  CAMPO_VAGAS,
  DIAS_SEMANA,
  TURNOS,
  STATUS_LABEL,
  formatDataBR,
  formatDataHora,
  nomeEscala,
} from './escalaUtils';

const normalizar = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** "3 turnos no ciclo" — só contagem; a escala não mexe em hora-aula. */
const cargaTexto = (p) => {
  if (!p) return 'nenhum turno no ciclo';
  return `${p.confirmados} turno${p.confirmados === 1 ? '' : 's'} no ciclo`;
};

/**
 * Editor de um dia da escala: vagas por turno, feriado, descrição e — por
 * turno — quem está confirmado, quem aguarda e a escalação direta.
 * As ações de pessoa (escalar/confirmar/recusar/remover) vão para o pai,
 * que recarrega o ciclo e devolve o `dia` atualizado.
 */
const DiaEditorModal = ({
  ciclo,
  dia,
  candidatos,
  pessoas,
  busy,
  onClose,
  onSalvarDia,
  onEscalar,
  onDecidir,
}) => {
  const [form, setForm] = useState({
    vagasManha: dia.vagasManha,
    vagasTarde: dia.vagasTarde,
    vagasNoite: dia.vagasNoite,
    feriado: dia.feriado,
    descricao: dia.descricao || '',
  });
  const [turnoEscalar, setTurnoEscalar] = useState(null);
  const [busca, setBusca] = useState('');
  const [recusa, setRecusa] = useState(null); // { inscricao, observacao }

  // Só reinicia o formulário quando muda o DIA — recarregar o mesmo dia
  // depois de escalar alguém não pode apagar uma vaga digitada e não salva.
  useEffect(() => {
    setForm({
      vagasManha: dia.vagasManha,
      vagasTarde: dia.vagasTarde,
      vagasNoite: dia.vagasNoite,
      feriado: dia.feriado,
      descricao: dia.descricao || '',
    });
    setTurnoEscalar(null);
    setBusca('');
    setRecusa(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dia.id]);

  const cargaPorPessoa = useMemo(() => {
    const m = new Map();
    (pessoas || []).forEach((p) => m.set(p.userId, p));
    return m;
  }, [pessoas]);

  const alterado =
    form.vagasManha !== dia.vagasManha ||
    form.vagasTarde !== dia.vagasTarde ||
    form.vagasNoite !== dia.vagasNoite ||
    form.feriado !== dia.feriado ||
    (form.descricao || '') !== (dia.descricao || '');

  const salvar = () =>
    onSalvarDia({
      vagasManha: Number(form.vagasManha),
      vagasTarde: Number(form.vagasTarde),
      vagasNoite: Number(form.vagasNoite),
      feriado: form.feriado,
      descricao: form.descricao.trim() || null,
    });

  const candidatosFiltrados = (turno) => {
    const ocupados = new Set([
      ...turno.confirmados.map((c) => c.userId),
      ...(turno.pendentes || []).map((c) => c.userId),
    ]);
    const termo = normalizar(busca);
    return (candidatos || [])
      .filter((c) => !ocupados.has(c.userId))
      .filter((c) => !termo || normalizar(`${c.nome} ${c.matricula || ''} ${c.posto || ''}`).includes(termo))
      .slice(0, 8);
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal esc-modal-dia" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>
              {DIAS_SEMANA[dia.diaSemana]}, {formatDataBR(dia.data)}
            </h2>
            <p className="esc-modal-sub">
              {ciclo.nome}
              {dia.passado && ' · dia já passou'}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="esc-modal-dia-corpo">
          {/* ---------- configuração do dia ---------- */}
          <section className="esc-dia-config">
            <h3>Vagas do dia</h3>
            <label className={`esc-switch ${form.feriado ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={form.feriado}
                onChange={(e) => setForm({ ...form, feriado: e.target.checked })}
              />
              <span className="esc-switch-track">
                <span className="esc-switch-thumb" />
              </span>
              <span className="esc-switch-texto">
                Feriado / sem expediente
                <small>Zera as vagas do dia e mostra FERIADO no calendário.</small>
              </span>
            </label>

            <div className="form-group">
              <label htmlFor="esc-dia-desc">Descrição</label>
              <input
                id="esc-dia-desc"
                value={form.descricao}
                maxLength={120}
                placeholder={form.feriado ? 'Ex.: Independência' : 'Ex.: Formatura, Conselho de classe'}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              />
            </div>

            {!form.feriado && (
              <div className="esc-dia-vagas">
                {TURNOS.map((t) => (
                  <div key={t.key} className={`esc-dia-vaga esc-turno-cor-${t.key}`}>
                    <span>{t.label}</span>
                    <Stepper
                      value={form[CAMPO_VAGAS[t.key]]}
                      onChange={(v) => setForm({ ...form, [CAMPO_VAGAS[t.key]]: v })}
                      ariaLabel={`vagas ${t.label}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <button className="btn-primary" onClick={salvar} disabled={busy || !alterado}>
              {busy ? 'Salvando…' : 'Salvar dia'}
            </button>
            {ciclo.modo === 'ordem_chegada' && (
              <small className="form-hint">
                Ordem de chegada: ao abrir vagas, quem está na fila é confirmado automaticamente.
              </small>
            )}
          </section>

          {/* ---------- pessoas por turno ---------- */}
          <section className="esc-dia-turnos">
            {dia.feriado ? (
              <div className="esc-dia-turno-vazio">
                <strong>Feriado</strong>
                <span>Não há vagas neste dia.</span>
              </div>
            ) : (
              TURNOS.map((t) => {
                const turno = dia.turnos.find((x) => x.turno === t.key);
                if (!turno) return null;
                const semNada =
                  turno.vagas === 0 && turno.confirmados.length === 0 && turno.pendentesCount === 0;
                if (semNada) {
                  return (
                    <div key={t.key} className="esc-dia-turno-vazio">
                      <strong>{t.label}</strong>
                      <span>Sem vagas neste turno.</span>
                    </div>
                  );
                }
                const lista = candidatosFiltrados(turno);
                const cheio = turno.livres === 0;
                return (
                  <div key={t.key} className={`esc-turno-painel esc-turno-cor-${t.key}`}>
                    <header className="esc-turno-painel-cab">
                      <span className="esc-turno-painel-nome">{t.label}</span>
                      <span className={`esc-cap ${cheio ? 'cheio' : ''}`}>
                        {turno.confirmados.length}/{turno.vagas}
                      </span>
                      <button
                        type="button"
                        className="esc-link-btn"
                        onClick={() => {
                          setTurnoEscalar(turnoEscalar === t.key ? null : t.key);
                          setBusca('');
                        }}
                        disabled={busy}
                      >
                        <PlusIcon /> Escalar
                      </button>
                    </header>

                    {turnoEscalar === t.key && (
                      <div className="esc-picker">
                        <div className="esc-picker-busca">
                          <MagnifyingGlassIcon />
                          <input
                            autoFocus
                            placeholder="Buscar militar por nome ou matrícula…"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                          />
                        </div>
                        {cheio && (
                          <small className="esc-warn">
                            Turno cheio — aumente as vagas do dia para escalar mais alguém.
                          </small>
                        )}
                        <ul className="esc-picker-lista">
                          {lista.map((c) => (
                            <li key={c.userId}>
                              <button
                                type="button"
                                disabled={busy || cheio}
                                onClick={() => onEscalar(t.key, c.userId)}
                              >
                                <span className="esc-picker-nome">
                                  {nomeEscala(c, true)}
                                  {!c.itinerante && <em>não é itinerante</em>}
                                </span>
                                <span className="esc-picker-carga">{cargaTexto(c)}</span>
                              </button>
                            </li>
                          ))}
                          {lista.length === 0 && (
                            <li className="esc-picker-vazio">Ninguém encontrado.</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <ul className="esc-pessoas">
                      {turno.confirmados.map((c) => (
                        <li key={c.id} className="esc-pessoa confirmado">
                          <span className="esc-pessoa-nome">
                            {nomeEscala(c, true)}
                            <small>
                              {c.origem === 'admin' ? 'escalado' : 'pediu a vaga'}
                              {c.decididoPorNome ? ` · ${c.decididoPorNome}` : ''}
                              {' · '}
                              {cargaTexto(cargaPorPessoa.get(c.userId))}
                            </small>
                          </span>
                          <button
                            type="button"
                            className="esc-icone-btn perigo"
                            title="Remover da escala"
                            disabled={busy}
                            onClick={() => onDecidir(c, 'cancelado')}
                          >
                            <TrashIcon />
                          </button>
                        </li>
                      ))}
                      {Array.from({ length: turno.livres }).map((_, i) => (
                        <li key={`livre-${i}`} className="esc-pessoa livre">
                          vaga livre
                        </li>
                      ))}
                    </ul>

                    {(turno.pendentes || []).length > 0 && (
                      <div className="esc-fila-bloco">
                        <span className="esc-fila-titulo">
                          Aguardando ({turno.pendentes.length})
                          {ciclo.modo === 'ordem_chegada' && <em> · na ordem de chegada</em>}
                        </span>
                        <ul className="esc-pessoas">
                          {turno.pendentes.map((p, idx) => (
                            <li key={p.id} className="esc-pessoa pendente">
                              <span className="esc-pessoa-ordem">{idx + 1}º</span>
                              <span className="esc-pessoa-nome">
                                {nomeEscala(p, true)}
                                <small>
                                  pediu em {formatDataHora(p.inscritoEm)} ·{' '}
                                  {cargaTexto(cargaPorPessoa.get(p.userId))}
                                </small>
                              </span>
                              <span className="esc-pessoa-acoes">
                                <button
                                  type="button"
                                  className="esc-icone-btn ok"
                                  title={cheio ? 'Sem vaga livre' : 'Confirmar'}
                                  disabled={busy || cheio}
                                  onClick={() => onDecidir(p, 'confirmado')}
                                >
                                  <CheckIcon />
                                </button>
                                <button
                                  type="button"
                                  className="esc-icone-btn perigo"
                                  title="Recusar"
                                  disabled={busy}
                                  onClick={() => setRecusa({ inscricao: p, observacao: '' })}
                                >
                                  <Cross2Icon />
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(turno.historico || []).length > 0 && (
                      <details className="esc-historico">
                        <summary>Histórico ({turno.historico.length})</summary>
                        <ul className="esc-pessoas">
                          {turno.historico.map((h) => (
                            <li key={h.id} className={`esc-pessoa ${h.status}`}>
                              <span className="esc-pessoa-nome">
                                {nomeEscala(h, true)}
                                <small>
                                  {STATUS_LABEL[h.status]}
                                  {h.decididoPorNome ? ` · ${h.decididoPorNome}` : ''}
                                  {h.observacao ? ` · "${h.observacao}"` : ''}
                                </small>
                              </span>
                              {h.status === 'recusado' && (
                                <button
                                  type="button"
                                  className="esc-icone-btn"
                                  title="Devolver à fila"
                                  disabled={busy}
                                  onClick={() => onDecidir(h, 'pendente')}
                                >
                                  <ReloadIcon />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })
            )}
          </section>
        </div>

        {recusa && (
          <div className="modal-overlay esc-sub-overlay" onClick={() => setRecusa(null)}>
            <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Recusar pedido</h2>
                <button className="modal-close" onClick={() => setRecusa(null)} aria-label="Fechar">
                  &times;
                </button>
              </div>
              <div className="confirmation-body">
                <p>
                  <strong>{nomeEscala(recusa.inscricao, true)}</strong> não vai neste turno. A pessoa vê
                  “não selecionado” e só volta à fila se você devolver.
                </p>
                <div className="form-group">
                  <label htmlFor="esc-recusa-obs">Motivo (opcional, fica visível para a pessoa)</label>
                  <input
                    id="esc-recusa-obs"
                    autoFocus
                    maxLength={500}
                    value={recusa.observacao}
                    onChange={(e) => setRecusa({ ...recusa, observacao: e.target.value })}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setRecusa(null)}>
                    Voltar
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={busy}
                    onClick={async () => {
                      await onDecidir(recusa.inscricao, 'recusado', recusa.observacao.trim() || null);
                      setRecusa(null);
                    }}
                  >
                    Recusar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiaEditorModal;
